import { defineStore } from 'pinia'
import type {
  TImageFolder,
  TSearchImage,
  TUserImage,
  TImagesPagination,
  TSortOptions,
  TUserSortOptions,
  TUploadFile,
  TUserImagesResponse,
  TCollectionImagesResponse,
  TSearchImagesResponse,
  TCachedImageSearch,
  TCollectionResponse,
} from '@/types/images'
import { useUserStore } from '@/stores/user'
import { useStorage } from '@vueuse/core'
import { http } from '@/helpers/http'
import { ref } from 'vue'
import { useSnackbarStore } from '@/stores/snackbar'
import { createBrowserCache } from '@/helpers/browserCache'

export const useImagesStore = defineStore('images', () => {
  const user = useUserStore()
  const snack = useSnackbarStore()
  const folders = ref<TImageFolder[]>([])
  const foldersSortBy = ref<string>('newest')
  const folderSortBy = ref<string>('newest')
  const folder = ref<TImageFolder | null>(null)
  const userImages = ref<TUserImage[]>([])
  // TODO: move page logic from pagination result
  const userImagesPage = ref<number>(1)
  const userImagesPagination = ref<TImagesPagination | null>(null)
  const userImagesSortBy = ref<string>('newest')
  const favoritesSortBy = ref<string>('newest')
  const searchImages = ref<TSearchImage[]>([])
  const generatedImages = ref<TUserImage[]>([])
  const searchImagesPage = ref<number>(1)
  const searchImagesQuery = ref<string>('business')
  const generateImagesQuery = ref<string>('')
  const sortOptions = ref<TUserSortOptions>({})
  const filesToUpload = ref<TUploadFile[]>([])
  const skipImages = ref<string[]>([])
  // const filesToUploadStatus = ref<TUploadFileStatus[]>([]);
  const uploading = ref<boolean>(false)
  const wgFolder = ref<TImageFolder | null>(null)
  const searchPagesCache = createBrowserCache<TCachedImageSearch>({
    name: 'image-search',
    version: 1,
    maxAgeMs: 24 * 60 * 60 * 1000,
    maxEntries: 10,
    keep: (res) => res.status === 'success' && res.data.length > 0,
  })
  const maxCachedSearchPages = 3
  let latestSearch = 0
  let searchBusy = false

  async function resetFilesToUpload() {
    filesToUpload.value = []
  }

  const acceptedImageTypes = ['image/jpeg', 'image/png', 'image/gif']

  async function addFilesToUpload(files: File[] | null) {
    if (!files) return

    const accepted = files.filter((file) => acceptedImageTypes.includes(file.type))
    const rejected = files.length - accepted.length
    if (rejected > 0) {
      snack.add(`${rejected} file(s) skipped. Only JPG, PNG, and GIF are supported.`)
    }

    accepted.forEach((file) => {
      filesToUpload.value.push({
        progress: 0,
        uploaded: false,
        uploading: false,
        file,
      })
    })
  }

  async function removeFileFromUpload(file: File) {
    const index = filesToUpload.value.findIndex((item) => file === item.file)
    if (index > -1) {
      filesToUpload.value.splice(index, 1)
    }
  }

  async function uploadFiles(folderId: number = 0): Promise<string[]> {
    const uploadedImages: string[] = []
    if (!filesToUpload.value) return uploadedImages

    uploading.value = true

    for (const item of filesToUpload.value) {
      item.progress = 0
      item.uploading = true

      try {
        await new Promise(function (resolve, reject) {
          const xhr = new XMLHttpRequest()
          const formData = new FormData()

          xhr.open('POST', '/images/')
          formData.append('file', item.file)
          formData.append('folderId', folderId.toString())
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              const response = JSON.parse(xhr.response)
              if (response[0]?.file_name) {
                uploadedImages.push(response[0].file_name)
              }
              resolve(xhr.response)
            } else {
              try {
                const response = JSON.parse(xhr.response)
                response && snack.add(response.message)
              } catch {
                // parsing response failed
              }
              reject(xhr.statusText)
            }
          }
          xhr.onerror = () => reject(xhr.statusText)
          xhr.upload.onprogress = (e: ProgressEvent) => {
            item.progress = e.total === 0 ? 0 : Math.ceil((e.loaded / e.total) * 100)
          }
          xhr.send(formData)
        })
        item.uploaded = true
      } catch (e: any) {
        console.error('handle error', e)
        item.error = e
        return uploadedImages
      }
      item.uploading = false
    }

    uploading.value = false
    filesToUpload.value = []

    return uploadedImages
  }

  async function fetchFolders(sortBy: string = '', withRoot = false) {
    sortOptions.value = useStorage('sortOptions', {} as TUserSortOptions).value

    if (sortBy !== '') {
      folderSortBy.value = sortBy
      if (!sortOptions.value[user.user.id]) {
        sortOptions.value[user.user.id] = <TSortOptions>{}
      }
      sortOptions.value[user.user.id]!.collections = sortBy
    } else {
      folderSortBy.value = sortOptions.value[user.user.id]?.collections || 'newest'
    }

    const res = await http.get<TImageFolder[]>(
      `/images/collections/${folderSortBy.value}${withRoot === true ? '/true' : ''}`,
    )
    if (!res) {
      throw new Error(`Failed to fetch folders`)
    }

    folders.value = res

    wgFolder.value = res.find((f) => f.name === 'Wordgenie') ?? null
  }

  function searchCacheKey(size: string): string {
    return `${searchImagesQuery.value.toLowerCase()}|${size}`
  }

  function requestSearchPage(page: number, size: string) {
    return http.post<TSearchImagesResponse>(`/imagesearch`, {
      query: searchImagesQuery.value,
      page,
      orientation: size !== 'all' ? size : null,
    })
  }

  function acceptedSearchPage(res: TSearchImagesResponse | null | undefined): TSearchImage[] | null {
    if (!res) {
      throw new Error('Fetching images error')
    }

    if (res.status === 'error') {
      snack.add(res.message || 'Failed to search images')
      return null
    }

    if (res.status !== 'success') {
      snack.add('Unexpected error while searching images')
      return null
    }

    return res.data
  }

  async function fetchSearchImages(nextPage = false, size = 'all') {
    if (nextPage) return fetchNextSearchImages(size)

    const search = ++latestSearch

    searchImagesPage.value = 1
    searchImages.value = []
    searchBusy = false

    if (!searchImagesQuery.value) return

    searchBusy = true

    try {
      const res = await searchPagesCache.remember(searchCacheKey(size), () => requestSearchPage(1, size))

      if (search !== latestSearch) return

      const data = acceptedSearchPage(res)

      if (!data) return

      searchImages.value = data
      searchImagesPage.value = res.lastPage ?? 1
    } finally {
      if (search === latestSearch) searchBusy = false
    }
  }

  async function fetchNextSearchImages(size: string) {
    if (searchBusy || !searchImagesQuery.value) return

    const search = latestSearch
    const page = searchImagesPage.value + 1
    const key = searchCacheKey(size)

    searchBusy = true

    try {
      const data = acceptedSearchPage(await requestSearchPage(page, size))

      if (search !== latestSearch || !data) return

      const shown = new Set(searchImages.value.map((image) => image.url))
      const added = data.filter((image) => !shown.has(image.url))

      searchImages.value = searchImages.value.concat(added)
      searchImagesPage.value = page

      if (page > maxCachedSearchPages) return

      searchPagesCache.update(key, (cached) =>
        (cached.lastPage ?? 1) === page - 1 ? { ...cached, data: cached.data.concat(added), lastPage: page } : cached,
      )
    } finally {
      if (search === latestSearch) searchBusy = false
    }
  }

  async function fetchNextUserImages() {
    userImagesPage.value++
    return await fetchUserImages(true)
  }

  function setSearchImagesQuery(query: string) {
    searchImagesQuery.value = query
  }
  function setGenerateImagesQuery(query: string) {
    generateImagesQuery.value = query
  }

  /**
   * Replace each of the search and generate queries that still holds `previous` with `keywords`, leaving the ones
   * the user has typed into, and return whether the search query was replaced.
   */
  function seedQueries(previous: string, keywords: string): boolean {
    const seedsSearch = searchImagesQuery.value === previous

    if (seedsSearch) searchImagesQuery.value = keywords
    if (generateImagesQuery.value === previous) generateImagesQuery.value = keywords

    return seedsSearch
  }
  async function fetchUserImages(nextPage = false, sortBy: string = '') {
    sortOptions.value = useStorage('sortOptions', {} as TUserSortOptions).value
    folder.value = null

    if (sortBy !== '') {
      userImagesSortBy.value = sortBy
      if (!sortOptions.value[user.user.id]) {
        sortOptions.value[user.user.id] = <TSortOptions>{}
      }
      sortOptions.value[user.user.id]!.myUploads = sortBy
    } else {
      userImagesSortBy.value = sortOptions.value[user.user.id]?.myUploads || 'newest'
    }

    if (nextPage === false) {
      userImagesPage.value = 1
      userImages.value = []
      userImagesPagination.value = null
    }

    const res = await http.get<TUserImagesResponse>(
      `/images/my-uploads/${userImagesPage.value}/${userImagesSortBy.value}`,
    )
    if (!res) {
      throw new Error(`Failed to fetch user images`)
    }

    if (userImagesPage.value > 1) {
      userImages.value = userImages.value.concat(res.results)
    } else {
      userImages.value = res.results
    }

    userImagesPagination.value = res.pagination
  }

  async function fetchFavoritesImages(nextPage = false, sortBy: string = '') {
    sortOptions.value = useStorage('sortOptions', {} as TUserSortOptions).value

    if (sortBy !== '') {
      favoritesSortBy.value = sortBy
      if (!sortOptions.value[user.user.id]) {
        sortOptions.value[user.user.id] = <TSortOptions>{}
      }
      sortOptions.value[user.user.id]!.favorites = sortBy
    } else {
      favoritesSortBy.value = sortOptions.value[user.user.id]?.favorites || 'newest'
    }

    if (nextPage === false) {
      userImagesPage.value = 1
      userImages.value = []
      userImagesPagination.value = null
    }

    const res = await http.get<TUserImagesResponse>(
      `/images/favorites/${userImagesPage.value}/${favoritesSortBy.value}`,
    )
    if (!res) {
      throw new Error(`Failed to fetch favorites images`)
    }

    if (userImagesPage.value > 1) {
      userImages.value = userImages.value.concat(res.results)
    } else {
      userImages.value = res.results
    }

    userImagesPagination.value = res.pagination
  }

  async function fetchNextFavoritesImages() {
    userImagesPage.value++
    return await fetchFavoritesImages(true)
  }

  async function fetchCollectionImages(id: number, nextPage = false, sortBy: string = '') {
    sortOptions.value = useStorage('sortOptions', {} as TUserSortOptions).value

    if (sortBy !== '') {
      folderSortBy.value = sortBy
      if (!sortOptions.value[user.user.id]) {
        sortOptions.value[user.user.id] = <TSortOptions>{}
      }
      sortOptions.value[user.user.id]!.collection = sortBy
    } else {
      folderSortBy.value = sortOptions.value[user.user.id]?.collection || 'newest'
    }

    if (nextPage === false) {
      userImagesPage.value = 1
      userImages.value = []
      folder.value = null
      userImagesPagination.value = null
    }

    const res = await http.get<TCollectionImagesResponse>(
      `/images/collection/${id}/${userImagesPage.value}/${folderSortBy.value}`,
    )
    if (!res) {
      throw new Error(`Failed to fetch collections images`)
    }

    if (userImagesPage.value > 1) {
      userImages.value = userImages.value.concat(res.results)
    } else {
      userImages.value = res.results
    }

    userImagesPagination.value = res.pagination
    folder.value = res.folder
  }

  async function fetchNextCollectionImages(id: number) {
    userImagesPage.value++
    return await fetchCollectionImages(id, true)
  }

  async function toggleFavorites(id: number, value: boolean) {
    return await http.post<{ result: number }>('/images/favorites', { id, value })
  }

  function removeUserImage(id: number) {
    const index = userImages.value.findIndex((image) => image.id === id)

    if (index >= 0) {
      userImages.value.splice(index, 1)
    }
  }

  async function deleteImage(id: number) {
    const index = userImages.value.findIndex((image) => image.id === id)
    const generatedIndex = generatedImages.value.findIndex((image) => image.id === id)
    if (!(index >= 0 || generatedIndex >= 0)) {
      return
    }
    if (index >= 0) {
      userImages.value.splice(index, 1)
      if (userImagesPagination?.value) {
        userImagesPagination.value.total--
      }
    }
    if (generatedIndex >= 0) {
      generatedImages.value.splice(index, 1)
    }
    const res = await http.delete(`/images/${id}`)
    if (!res) {
      throw new Error(`Failed to delete image`)
    }
  }

  async function deleteImages(images: TUserImage[]) {
    images.forEach((image) => {
      const index = userImages.value.findIndex((userImage) => userImage.id === image.id)
      if (index >= 0) {
        if (userImagesPagination?.value) {
          userImagesPagination.value.total--
        }
        userImages.value.splice(index, 1)
      }
    })

    const res = await http.post('/images_delete_multiple', { images })
    if (!res) {
      throw new Error(`Failed to delete images`)
    }
  }

  async function deleteCollection(id: number, withImages: boolean = false) {
    const res = await http.delete(`/folders/with${withImages === true ? '' : 'out'}Images/${id}`)

    if (!res) {
      throw new Error(`Failed to delete collection`)
    }
    fetchFolders()
  }

  async function deleteCollections(collections: TImageFolder[], withImages: boolean = false) {
    const ids = collections.map((collection) => collection.id)
    const res = await http.post(`/folders/delete${withImages === true ? '/true' : ''}`, { ids })

    if (!res) {
      throw new Error(`Failed to delete collection`)
    }
    fetchFolders()
  }

  async function createCollection(name: string) {
    const res = await http.post<TCollectionResponse>('/folders', { name })

    if (!res) {
      throw new Error(`Failed to create collection`)
    }
    fetchFolders()
    return res
  }

  async function renameCollection(folderId: number, name: string) {
    const res = await http.post<TCollectionResponse>(`/folders/rename/${folderId}`, { name })

    if (!res) {
      throw new Error(`Failed to rename collection`)
    }
    return res
  }

  async function moveToCollection(image: TUserImage | TSearchImage, collectionId: number) {
    if ('id' in image) {
      if (image.id === undefined || image.id === null) {
        const error = new Error('moveToCollection called with undefined image.id')
        window?.Bugsnag?.notify(error, (event: { addMetadata: (key: string, data: object) => void }) => {
          event.addMetadata('context', {
            imageId: image.id,
            collectionId,
            imageKeys: Object.keys(image),
            hasId: 'id' in image,
          })
        })
        throw error
      }
      const res = await http.post(`/images/move_to_folder/${image.id}/${collectionId === -1 ? 0 : collectionId}`)
      if (!res) {
        throw new Error(`Failed to move to collection`)
      }
      const index = userImages.value.findIndex((item) => item.id === image.id)
      if (userImagesPagination.value) {
        userImagesPagination.value.total--
      }
      userImages.value.splice(index, 1)
    } else {
      return await copyStockImage(image, collectionId)
    }
  }

  async function saveImage(url: string, folderId?: number, favorite: boolean = false): Promise<TUserImage> {
    const result = await http.post<TUserImage>('/images/store_aws', {
      ...(folderId && { folderId }),
      url,
      favorite,
    })

    if (!result) {
      throw new Error(`Failed to store in AWS`)
    }
    userImages.value?.unshift(result)
    if (userImagesPagination?.value) {
      userImagesPagination.value.total++
    }
    return result
  }

  async function copyStockImage(image: TSearchImage, folderId?: number): Promise<TUserImage> {
    const url = getFullURL(image)
    return saveImage(url, folderId)
  }

  async function moveToCollectionMany(images: TUserImage[], folder: TImageFolder) {
    const res = await http.post('/images/move_to_folder_multiple', { images, folder })

    if (!res) {
      throw new Error(`Failed to move many to collection`)
    }

    images.forEach((image) => {
      const index = userImages.value.findIndex((userImage) => userImage.id === image.id)

      if (index >= 0) {
        if (userImagesPagination?.value) {
          userImagesPagination.value.total--
        }
        userImages.value.splice(index, 1)
      }
    })
  }

  function getFullURL(image: TUserImage | TSearchImage) {
    const postfix = image.url.indexOf('unsplash.com') >= 0 && 'title' in image ? `&id=${image.title}` : ''
    return image.url + postfix
  }

  async function getImagesByKeywords(keywords: string, type: string): Promise<TUserImage | null> {
    setSearchImagesQuery(keywords)
    await fetchSearchImages(false, type)

    if (searchImages.value.length === 0) {
      throw new Error('No images found')
    }

    if (skipImages.value && skipImages.value.length > 0) {
      let selectedImage = searchImages.value.find((img) => {
        const imgId = extractImageId(img.url)
        return !skipImages.value.some((skipUrl) => extractImageId(skipUrl) === imgId)
      })

      if (!selectedImage) {
        selectedImage = searchImages.value[searchImages.value.length - 1]
      }

      return copyStockImage(selectedImage!)
    } else {
      return copyStockImage(searchImages.value[0]!)
    }
  }

  function extractImageId(url: string): string | null {
    const match = url.match(/photo-([a-zA-Z0-9]+-[a-zA-Z0-9]+)/)
    return match?.[1] ? match[1] : null
  }

  async function fetchGeneratedImages() {
    if (!wgFolder.value || generatedImages.value.length > 0) {
      return
    }

    const res = await http.get<TUserImagesResponse>(`/images/collection/${wgFolder.value.id}`)
    if (!res) {
      return
    }

    generatedImages.value = res.results
  }

  return {
    folders,
    foldersSortBy,
    folderSortBy,
    folder,
    wgFolder,
    userImages,
    userImagesPage,
    userImagesPagination,
    userImagesSortBy,
    favoritesSortBy,
    searchImages,
    generatedImages,
    searchImagesPage,
    searchImagesQuery,
    generateImagesQuery,
    sortOptions,
    filesToUpload,
    uploading,
    resetFilesToUpload,
    addFilesToUpload,
    removeFileFromUpload,
    uploadFiles,
    fetchFolders,
    fetchSearchImages,
    fetchNextSearchImages,
    fetchNextUserImages,
    setSearchImagesQuery,
    setGenerateImagesQuery,
    seedQueries,
    fetchUserImages,
    fetchFavoritesImages,
    fetchNextFavoritesImages,
    fetchCollectionImages,
    fetchNextCollectionImages,
    fetchGeneratedImages,
    toggleFavorites,
    removeUserImage,
    deleteImage,
    deleteImages,
    deleteCollection,
    deleteCollections,
    createCollection,
    renameCollection,
    moveToCollection,
    moveToCollectionMany,
    getFullURL,
    copyStockImage,
    saveImage,
    getImagesByKeywords,
    skipImages,
  }
})
